import { Component, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { FormControl } from '@angular/forms';
import { NgbDate, NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import * as moment from 'moment';
import { DATE_FILTER_OPTIONS } from 'src/app/global/constants';

@Component({
  selector: 'app-date-picker',
  templateUrl: './date-picker.component.html',
  styleUrls: ['./date-picker.component.scss']
})
export class DatePickerComponent implements OnInit {
  //input variables
  @Input() disable: boolean;
  // Get reference to the ng-template modal
  @ViewChild('calendarTemplate') calendarTemplate!: ElementRef;

  // Selected date range type (e.g. Today, Last 7 Days, Custom, etc.)
  selectedType: string = '';

  // Variables for storing selected 'from' and 'to' dates
  fromDate?: NgbDate;
  toDate?: NgbDate;

  // Today's date in NgbDate format, used as maxDate
  today = this.toNgbDate(new Date());

  // Reference to the currently open modal (used to close it)
  modalRef?: NgbModalRef;

  // List of date filter options (should be imported from global/constants)
  dateFilterList = DATE_FILTER_OPTIONS;

  @Output() dateSelected = new EventEmitter<any>();
  dateControl: FormControl = new FormControl('');
  constructor(private modalService: NgbModal) { }

  ngOnInit(): void { }

  /**
   * Open the calendar modal
   */
  openModal(): void {
    this.modalRef = this.modalService.open(this.calendarTemplate, {
      centered: true,
      backdrop: 'static',
      keyboard: false
    });
  }

  /**
   * Close the calendar modal
   */
  closeModal(): void {
    this.modalRef?.close();
  }

  /**
   * Called when user selects any date
   * Ensures 'toDate' is never before 'fromDate'
   */
  onDateSelect(): void {
    if (this.fromDate && this.toDate && this.compareDates(this.fromDate, this.toDate) > 0) {
      this.toDate = this.fromDate; // Auto-correct invalid date range
    }
  }

  /**
   * Called when user clicks the "OK" button in modal
   * Formats and logs the selected date range
   */
  submit(): void {
    const payload = {
      type: this.selectedType,
      from: '',
      to: ''
    };

    // If "Custom" range selected, include from and to dates
    if (this.selectedType === 'Custom' && this.fromDate && this.toDate) {
      payload.from = this.formatDate(this.fromDate, false); // Start of day
      payload.to = this.formatDate(this.toDate, true);      // End of day
    }

    this.dateSelected.emit(payload);
    this.dateControl.setValue(this.selectedType == 'None'
      ? '' : this.selectedType === 'Custom'
        ? `${moment(payload.from).format('MM-DD-YYYY')} - ${moment(payload.to).format('MM-DD-YYYY')}`
        : this.selectedType);
    this.closeModal(); // Close the modal
  }

  /**
   * Converts NgbDate to formatted string
   * @param date NgbDate object
   * @param endOfDay whether to set time to 23:59:59
   * @returns formatted date string
   */
  private formatDate(date: NgbDate, endOfDay: boolean): string {
    const jsDate = new Date(date.year, date.month - 1, date.day, endOfDay ? 23 : 0, endOfDay ? 59 : 0, endOfDay ? 59 : 0);
    return moment(jsDate).format('YYYY-MM-DD HH:mm:ss');
  }

  /**
   * Converts JavaScript Date to NgbDate format
   * @param date JS Date object
   * @returns NgbDate
   */
  private toNgbDate(date: Date): any {
    return { year: date.getFullYear(), month: date.getMonth() + 1, day: date.getDate() };
  }

  /**
   * Compares two NgbDate values
   * @returns difference in milliseconds
   */
  private compareDates(d1: NgbDate, d2: NgbDate): number {
    const date1 = new Date(d1.year, d1.month - 1, d1.day).getTime();
    const date2 = new Date(d2.year, d2.month - 1, d2.day).getTime();
    return date1 - date2;
  }
}
